/**
 * Breadcrumbs Component
 * SEO-friendly breadcrumb navigation with schema markup
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { BreadcrumbItem } from '../data/seo';

interface BreadcrumbsProps {
  items: BreadcrumbItem[];
  className?: string;
}

const Breadcrumbs: React.FC<BreadcrumbsProps> = ({ items, className = '' }) => {
  if (items.length === 0) return null;

  return (
    <nav 
      aria-label="Breadcrumb" 
      className={`text-sm text-gray-500 ${className}`}
    >
      <ol className="flex flex-wrap items-center gap-1 sm:gap-2">
        {items.map((item, index) => {
          const isLast = index === items.length - 1;
          
          return (
            <li key={item.url} className="flex items-center">
              {index > 0 && (
                <span className="mx-1 sm:mx-2 text-gray-400" aria-hidden="true">
                  <i className="fas fa-chevron-right text-xs"></i>
                </span>
              )}
              
              {isLast ? (
                <span 
                  className="text-gray-700 font-medium truncate max-w-[200px] sm:max-w-none"
                  aria-current="page"
                >
                  {item.name}
                </span>
              ) : (
                <Link
                  to={item.url}
                  className="text-brand-teal hover:text-teal-700 hover:underline transition-colors truncate max-w-[150px] sm:max-w-none"
                >
                  {item.name}
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

/**
 * Helper to create common breadcrumb paths
 */
export const createBreadcrumbs = {
  home: (): BreadcrumbItem => ({ name: 'Home', url: '/' }),
  
  services: (): BreadcrumbItem => ({ name: 'Services', url: '/services' }),
  
  blog: (): BreadcrumbItem => ({ name: 'Blog', url: '/blog' }),
  
  locations: (): BreadcrumbItem => ({ name: 'Locations', url: '/south-florida-pet-dental' }),
  
  blogPost: (title: string, slug: string): BreadcrumbItem[] => [
    { name: 'Home', url: '/' },
    { name: 'Blog', url: '/blog' },
    { name: title, url: `/blog/${slug}` },
  ],
  
  location: (name: string, slug: string): BreadcrumbItem[] => [
    { name: 'Home', url: '/' },
    { name: 'Locations', url: '/south-florida-pet-dental' },
    { name: name, url: `/locations/${slug}` },
  ],
  
  service: (name: string, slug: string): BreadcrumbItem[] => [
    { name: 'Home', url: '/' },
    { name: 'Services', url: '/services' },
    { name: name, url: `/services/${slug}` },
  ],
  
  page: (name: string, url: string): BreadcrumbItem[] => [
    { name: 'Home', url: '/' },
    { name: name, url: url },
  ],
};

export default Breadcrumbs;



